import { useCallback, useEffect, useMemo, useReducer } from 'react'
import usePrevious from 'hooks/usePrevious'
import { arrayToObject, EmptySet, sortBy } from 'lib/array/arrayUtils'
import { objectKeys } from 'lib/object/objectUtils'
import type { TripDetail, TripDetailsCheck } from 'tripPlanner/api/tripDetails'
import { combineSelectionWithMessage } from 'tripPlanner/assistant/utils/messageEnrichment'
import { appendSentence } from 'tripPlanner/components/Chat/utils/transcript'
import {
  type NudgeUse,
  trackTripDetailsNudgeAnswered,
  trackTripDetailsNudgeDismissed,
  trackTripDetailsNudgeShown,
  trackTripDetailsSubmitted,
} from 'tripPlanner/utils/chatAnalytics'
import { hasDismissedTripDetailsNudge, markTripDetailsNudgeDismissed } from 'tripPlanner/utils/tripDetailsNudgeDismissal'
import type { NudgeOption } from './tripDetailsNudgeAnswers'
import { useTripDetailsCheck } from './useTripDetailsCheck'

/** The order details are asked about: what the first reply needs most comes first. */
const TRIP_DETAIL_RANK = {
  destination: 0,
  dates: 1,
  travellers: 2,
  budget: 3,
  preferences: 4,
} satisfies Record<TripDetail, number>

const TRIP_DETAIL_PRIORITY = sortBy(objectKeys(TRIP_DETAIL_RANK), detail => TRIP_DETAIL_RANK[detail], 'asc')

const NO_TAPPED_ANSWERS: ReadonlyMap<TripDetail, string> = new Map()

interface NudgeQuestion {
  detail: TripDetail
  showsAnswers: boolean
}

interface NudgeState {
  askedDetail?: TripDetail
  /** The asked question, once it's been on screen */
  shownDetail?: TripDetail
  /** Details this draft has shown questions about */
  shown: ReadonlySet<TripDetail>
  /** The sentence each tapped answer added; a tapped detail isn't asked again until the draft starts afresh */
  tapped: ReadonlyMap<TripDetail, string>
  /** Details given in the traveller's own words once asked; deleting one lets it be asked again */
  saidInOwnWords: ReadonlySet<TripDetail>
  /** Dismissing the nudge turns it off for the rest of the session, not just that question */
  isDismissed: boolean
  /** The draft as the last tapped answer left it */
  answeredText?: string
  /** The details that draft gives, known without a check, since an answer's sentence only adds the detail it answers */
  answeredDetails?: TripDetailsCheck
  /** The draft as the last transcript left it */
  transcriptText?: string
}

type NudgeAction =
  | { type: 'asked'; detail: TripDetail }
  | { type: 'shown'; detail: TripDetail }
  | { type: 'said_in_own_words'; detail: TripDetail; wasShown: boolean }
  | { type: 'tapped'; tapped: ReadonlyMap<TripDetail, string>; answeredText: string; answeredDetails?: TripDetailsCheck; next?: TripDetail }
  | { type: 'dismissed' }
  | { type: 'withdrawn' }
  | { type: 'draft_cleared' }
  | { type: 'transcript_landed'; transcriptText: string }

function createInitialState(): NudgeState {
  return { shown: EmptySet, tapped: NO_TAPPED_ANSWERS, saidInOwnWords: EmptySet, isDismissed: hasDismissedTripDetailsNudge() }
}

function nudgeReducer(state: NudgeState, action: NudgeAction): NudgeState {
  switch (action.type) {
    case 'asked':
      return { ...state, askedDetail: action.detail, shownDetail: undefined }
    case 'shown':
      return { ...state, shownDetail: action.detail, shown: new Set(state.shown).add(action.detail) }
    case 'said_in_own_words':
      return {
        ...state,
        askedDetail: undefined,
        shownDetail: undefined,
        saidInOwnWords: action.wasShown ? new Set(state.saidInOwnWords).add(action.detail) : state.saidInOwnWords,
      }
    case 'tapped':
      return {
        ...state,
        askedDetail: action.next,
        shownDetail: undefined,
        tapped: action.tapped,
        answeredText: action.answeredText,
        answeredDetails: action.answeredDetails,
      }
    case 'dismissed':
      return { ...state, askedDetail: undefined, shownDetail: undefined, isDismissed: true }
    case 'withdrawn':
      return state.askedDetail ? { ...state, askedDetail: undefined, shownDetail: undefined } : state
    case 'draft_cleared':
      return {
        ...state,
        askedDetail: undefined,
        shownDetail: undefined,
        shown: EmptySet,
        tapped: NO_TAPPED_ANSWERS,
        saidInOwnWords: EmptySet,
        answeredText: undefined,
        answeredDetails: undefined,
      }
    case 'transcript_landed':
      return { ...state, transcriptText: action.transcriptText }
  }
}

function findNextDetail(mentioned: TripDetailsCheck, tapped: ReadonlyMap<TripDetail, string>): TripDetail | undefined {
  return TRIP_DETAIL_PRIORITY.find(detail => !mentioned[detail] && !tapped.has(detail))
}

/** A tapped answer counts while its sentence is in the draft, even when the check doesn't read it as a mention. */
function withTappedAnswers(mentioned: TripDetailsCheck, tapped: ReadonlyMap<TripDetail, string>, message: string): TripDetailsCheck {
  const isTapped = (detail: TripDetail) => {
    const sentence = tapped.get(detail)
    return sentence !== undefined && message.includes(sentence)
  }
  return arrayToObject(objectKeys(mentioned), detail => detail, detail => mentioned[detail] || isTapped(detail))
}

interface DraftUse {
  /** Undefined unless the last check was of the text sent, or it's as a tapped answer left it */
  details?: TripDetailsCheck
  shownCount: number
  answeredCount: number
  isDismissed: boolean
  hasTyped: boolean
  isLongEnough: boolean
  isAvailable: boolean
}

function getNudgeUse({ details, shownCount, answeredCount, isDismissed, hasTyped, isLongEnough, isAvailable }: DraftUse): NudgeUse {
  if (answeredCount > 0) return 'answered'
  if (shownCount > 0) return 'unanswered'
  if (isDismissed) return 'dismissed_earlier'
  if (!hasTyped) return 'not_typed'
  if (!isLongEnough) return 'too_short'
  if (!isAvailable) return 'unavailable'
  if (!details) return 'not_checked'
  if (Object.values(details).every(Boolean)) return 'complete'
  return 'not_asked'
}

interface Options {
  message: string
  /** Chips, sent ahead of the message */
  selectedTags: Array<string>
  /** Recording or transcribing: the draft changes only once the transcript lands */
  isVoiceBusy: boolean
  disabled: boolean
  /** Called with the draft a tapped answer leaves */
  onMessageChange: (message: string) => void
}

interface TripDetailsNudgeResult {
  /** The question to show now */
  nudge?: NudgeQuestion
  /** The question being asked, for screen readers; silent while recording, so the mic doesn't pick it up */
  announcedDetail?: TripDetail
  /** The draft is as a tapped answer left it, so it arrived whole rather than being typed */
  isAnswered: boolean
  /** The question is on screen */
  markShown: (detail: TripDetail) => void
  answer: (detail: TripDetail, option: NudgeOption, sentence: string) => void
  dismiss: (detail: TripDetail) => void
  /** Logs the trip details the draft gives as it's sent */
  trackSubmitted: () => void
}

/** Which trip detail to ask about next while the customer types or speaks on the landing page. */
export function useTripDetailsNudge({ message, selectedTags, isVoiceBusy, disabled, onMessageChange }: Options): TripDetailsNudgeResult {
  const [state, dispatch] = useReducer(nudgeReducer, undefined, createInitialState)
  const { askedDetail, shownDetail, shown, tapped, saidInOwnWords, isDismissed, answeredDetails } = state
  const hasTyped = message.trim().length > 0
  // Chips count the way they're sent, ahead of what the traveller typed or said.
  const text = combineSelectionWithMessage(selectedTags, message)
  const wasVoiceBusy = usePrevious(isVoiceBusy, false)
  const hasTranscriptLanded = wasVoiceBusy && !isVoiceBusy
  const isAnswered = text === state.answeredText
  // Chips alone are never checked or asked about: nothing happens until the traveller has typed or spoken.
  const { mentioned, isCurrent, hasFailed, isLongEnough, isAvailable } = useTripDetailsCheck(hasTyped ? text : '', { isWhole: hasTranscriptLanded || isAnswered })
  const canAsk = hasTyped && !disabled
  // Only a checked draft asks, so it never asks about something just said or typed.
  const canPick = isCurrent && !isVoiceBusy
  // The transcript can answer the question, so answers wait for its check; once that fails or the draft is edited, it's like typing.
  const isAwaitingTranscriptCheck = !isCurrent && !hasFailed && (hasTranscriptLanded || text === state.transcriptText)

  const knownDetails = useMemo(() => {
    if (isCurrent) return withTappedAnswers(mentioned, tapped, message)
    if (isAnswered) return answeredDetails
    return undefined
  }, [answeredDetails, isAnswered, isCurrent, mentioned, message, tapped])

  useEffect(() => {
    if (hasTranscriptLanded) {
      dispatch({ type: 'transcript_landed', transcriptText: text })
    }
  }, [hasTranscriptLanded, text])

  useEffect(() => {
    // An emptied or sent draft starts afresh, so its answered questions can be asked again.
    if (!hasTyped) {
      dispatch({ type: 'draft_cleared' })
    }
  }, [hasTyped])

  useEffect(() => {
    if (!canAsk || !isAvailable || isDismissed) {
      dispatch({ type: 'withdrawn' })
      return
    }
    if (askedDetail) {
      if (mentioned[askedDetail]) {
        // A question the traveller never saw didn't prompt the answer.
        const wasShown = askedDetail === shownDetail
        dispatch({ type: 'said_in_own_words', detail: askedDetail, wasShown })
        if (wasShown) {
          trackTripDetailsNudgeAnswered(askedDetail, { via: 'own_words' })
        }
      }
      return
    }
    if (!canPick) return

    const next = findNextDetail(mentioned, tapped)
    if (next) {
      dispatch({ type: 'asked', detail: next })
    }
  }, [askedDetail, canAsk, canPick, isAvailable, isDismissed, mentioned, shownDetail, tapped])

  // The card can be off screen, such as while the composer is scrolled away, so a question counts as shown once the traveller can see it.
  const markShown = useCallback((detail: TripDetail) => {
    if (detail !== askedDetail || detail === shownDetail) return
    dispatch({ type: 'shown', detail })
    trackTripDetailsNudgeShown(detail)
  }, [askedDetail, shownDetail])

  const answer = useCallback((detail: TripDetail, option: NudgeOption, sentence: string) => {
    const nextMessage = appendSentence(message, sentence)
    const nextTapped = new Map(tapped).set(detail, sentence)
    // An answer's sentence only adds the detail it answers, so a checked draft, or one the last answer left, moves straight on.
    const next = canPick || isAnswered ? findNextDetail(mentioned, nextTapped) : undefined
    dispatch({
      type: 'tapped',
      tapped: nextTapped,
      answeredText: combineSelectionWithMessage(selectedTags, nextMessage),
      answeredDetails: knownDetails && { ...knownDetails, [detail]: true },
      next,
    })
    trackTripDetailsNudgeAnswered(detail, { via: 'answer', option })
    onMessageChange(nextMessage)
  }, [canPick, isAnswered, knownDetails, mentioned, message, onMessageChange, selectedTags, tapped])

  const dismiss = useCallback((detail: TripDetail) => {
    markTripDetailsNudgeDismissed()
    dispatch({ type: 'dismissed' })
    trackTripDetailsNudgeDismissed(detail)
  }, [])

  const trackSubmitted = useCallback(() => {
    const shownCount = shown.size
    const answeredCount = new Set([...tapped.keys(), ...saidInOwnWords]).size
    trackTripDetailsSubmitted({
      details: knownDetails,
      shownCount,
      answeredCount,
      nudgeUse: getNudgeUse({ details: knownDetails, shownCount, answeredCount, isDismissed, hasTyped, isLongEnough, isAvailable }),
    })
  }, [hasTyped, isAvailable, isDismissed, isLongEnough, knownDetails, saidInOwnWords, shown, tapped])

  // Typing hides the question until the next pause, so it never sits over words being written.
  const isSettled = isVoiceBusy || isAwaitingTranscriptCheck || isCurrent || isAnswered
  const nudge = useMemo(
    () => (askedDetail && isSettled ? { detail: askedDetail, showsAnswers: !isVoiceBusy && !isAwaitingTranscriptCheck } : undefined),
    [askedDetail, isAwaitingTranscriptCheck, isSettled, isVoiceBusy],
  )

  return {
    nudge,
    announcedDetail: isVoiceBusy ? undefined : askedDetail,
    isAnswered,
    markShown,
    answer,
    dismiss,
    trackSubmitted,
  }
}
